import clsx from "clsx";
import type { InputHTMLAttributes } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  characterEnvSelectors,
  PremadeCharacter,
  PremadeInfoStatus,
} from "@dndbeyond/character-rules-engine";
import ArrowLeft from "@dndbeyond/fontawesome-cache/svgs/solid/arrow-left.svg";
import FilterIcon from "@dndbeyond/fontawesome-cache/svgs/solid/filter.svg";
import { Button, ButtonProps } from "@dndbeyond/ttui/lexicon/components/Button";
import { Dropdown, Option } from "@dndbeyond/ttui/lexicon/components/Dropdown";
import { InlineAlert } from "@dndbeyond/ttui/lexicon/components/InlineAlert";
import { Search } from "@dndbeyond/ttui/lexicon/components/Search";
import { TabFilter } from "@dndbeyond/ttui/lexicon/components/TabFilter";
import { Toast } from "@dndbeyond/ttui/lexicon/components/Toast";

import { MaxCharactersDialog } from "~/components/MaxCharactersDialog";
import { NoResultsFound } from "~/components/NoResultsFound";
import { characterServiceBaseUrl } from "~/config";
import { ddbBaseUrl } from "~/config";
import { claimCharacter, joinCampaign } from "~/helpers/characterServiceApi";
import { summon } from "~/helpers/summon";
import {
  ClickElementInput,
  getBackToCampaignClick,
  getChangeLocation,
  getChoosePremadeClick,
  getPremadeFilterClick,
  getPremadeRulesetClick,
  getPremadeSubscribeClick,
  getPreviewPremadeClick,
  prepInstrumentation,
  sendClickElement,
} from "~/instrumentation";
import { NavigationUtils } from "~/tools/js/CharacterBuilder/utils";
import UserRoles from "~/tools/js/Shared/constants/UserRoles";
import { appEnvSelectors } from "~/tools/js/Shared/selectors";

import { Card } from "./Card";
import { FilterDrawer } from "./FilterDrawer";
import { Skeleton } from "./Skeleton";
import { ALL_RULESETS, RulesetSelection } from "./constants";
import {
  filterPremades,
  orderPremades,
  getFilterOptions,
  getPrimarySource,
  optionsFromParams,
} from "./helpers";
import styles from "./styles.module.css";

// Opts the input out of the global input styles in styles/global.css
const LEXICON_INPUT_PROPS: InputHTMLAttributes<HTMLInputElement> & {
  "data-lexicon": string;
} = { "data-lexicon": "true" };

export const PremadeListing = () => {
  const [premadeCharacters, setPremadeCharacters] = useState<
    Array<PremadeCharacter>
  >([]);
  const [loadingStatus, setLoadingStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [elevatedUserCharacters, setElevatedUserCharacters] = useState<
    Array<PremadeCharacter>
  >([]);
  const [showElevatedUserCharacters, setShowElevatedUserCharacters] =
    useState(false);
  const [showProgressForIndex, setShowProgressForIndex] = useState<
    number | null
  >(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<Option[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<Option[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<Option[]>([]);
  const [selectedSources, setSelectedSources] = useState<Option[]>([]);
  const [selectedRuleset, setSelectedRuleset] =
    useState<RulesetSelection>(ALL_RULESETS);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const appContext = useSelector(characterEnvSelectors.getContext);
  const userRoles = useSelector(appEnvSelectors.getUserRoles);
  const isElevatedUser =
    userRoles?.includes(UserRoles.LOREKEEPER) ||
    userRoles?.includes(UserRoles.ADMIN);

  const { characterSlotLimit, activeCharacterCount } = useSelector(
    appEnvSelectors.getCharacterSlots
  );
  const hasSlots =
    characterSlotLimit === null || activeCharacterCount < characterSlotLimit;

  const params = new URLSearchParams(globalThis.location.search);
  const campaignJoinCode = params.get("campaignJoinCode");
  const isAssigned = params.get("isAssigned") !== "false";
  const campaignId = params.get("campaignId");
  const backToCampaignUrl = `${ddbBaseUrl}/campaigns/${
    campaignJoinCode ? `join/${campaignJoinCode}` : campaignId
  }`;
  const subscribeUrl = `${ddbBaseUrl}/store/subscribe#plans`;

  const sendPremadeClick = (click: ClickElementInput): void => {
    sendClickElement(click, {
      location: getChangeLocation(appContext, window.location.pathname),
    });
  };

  // Most clicks here navigate away, so look up the IP before the first one
  useEffect(() => {
    prepInstrumentation();
  }, []);

  const filteredCharacters = useMemo(
    () =>
      filterPremades(premadeCharacters, {
        query,
        classes: selectedClasses,
        species: selectedSpecies,
        levels: selectedLevels,
        sources: selectedSources,
        ruleset: selectedRuleset,
      }),
    [
      premadeCharacters,
      query,
      selectedClasses,
      selectedSpecies,
      selectedLevels,
      selectedSources,
      selectedRuleset,
    ]
  );

  const onClaim = async (characterId: number, key: number) => {
    try {
      setShowProgressForIndex(key);
      const response = await claimCharacter(characterId, isAssigned);
      let clonedId: number;
      if (response.ok) {
        const { id } = await response.json();
        clonedId = id;
      } else {
        if (response.status === 406) {
          setShowDialog(true);
          setShowProgressForIndex(null);
          return;
        } else {
          throw new Error("Error when cloning premade character!");
        }
      }
      let campaignId: string | null = null;

      if (campaignJoinCode) {
        const campaignResponse = await joinCampaign(campaignJoinCode, clonedId);

        if (campaignResponse.ok) {
          const data = await campaignResponse.json();
          campaignId = data.campaignId;
        } else {
          throw new Error(
            "Error when attempting to attach cloned character to campaign!"
          );
        }
      }

      // Redirect to character page or campaign page
      const characterUrl = NavigationUtils.getCharacterSheetUrl(clonedId);
      if (isAssigned) {
        window.location = characterUrl as string & Location;
      } else {
        // If, somehow, the campaignId is null, send them to their new character sheet. Otherwise, send them
        // back to the campaign page.
        window.location = campaignId
          ? (`${ddbBaseUrl}/campaigns/${campaignId}` as string & Location)
          : (characterUrl as string & Location);
      }
    } catch (e) {
      setShowToast(true);
      setShowProgressForIndex(null);
      throw e;
    } finally {
      setShowProgressForIndex(null);
    }
  };

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        const req = await summon(
          `${characterServiceBaseUrl}/premade${
            campaignId ? `?campaignId=${campaignId}` : ""
          }`
        );
        if (req.ok) {
          const json: { data: Array<PremadeCharacter> } = await req.json();
          const premades = json.data;
          setPremadeCharacters(
            premades.filter(
              (char) => char.publishStatus === PremadeInfoStatus.PUBLISHED
            )
          );
          setElevatedUserCharacters(
            premades.filter(
              (char) => char.publishStatus !== PremadeInfoStatus.PUBLISHED
            )
          );
        } else {
          setLoadingStatus("error");
        }
      } catch {
        setLoadingStatus("error");
        throw new Error("Non-OK response from GET published premades");
      } finally {
        setLoadingStatus("loaded");
      }
    };
    fetchCharacters();
  }, [campaignId]);

  const filterOptions = useMemo(
    () => getFilterOptions(premadeCharacters),
    [premadeCharacters]
  );

  // With a Rule Set tab selected, only offer that ruleset's collections
  const collectionOptions = useMemo(
    () =>
      selectedRuleset === ALL_RULESETS
        ? filterOptions.sources
        : filterOptions.sources.filter(
            (option) => option.rulesetId === selectedRuleset
          ),
    [filterOptions.sources, selectedRuleset]
  );

  const filters = [
    {
      key: "collection",
      label: "Collection",
      options: collectionOptions,
      selected: selectedSources,
      onChange: setSelectedSources,
    },
    {
      key: "class",
      label: "Class",
      options: filterOptions.classes,
      selected: selectedClasses,
      onChange: setSelectedClasses,
    },
    {
      key: "level",
      label: "Level",
      options: filterOptions.levels,
      selected: selectedLevels,
      onChange: setSelectedLevels,
    },
    {
      key: "species",
      label: "Species",
      options: filterOptions.species,
      selected: selectedSpecies,
      onChange: setSelectedSpecies,
    },
  ];

  // Prefilter from search params (e.g. ?class=Wizard&level=1&collection=145)
  // once, after the premades load, so marketing and campaign links can land
  // on a filtered list. The URL is not updated as the filters change.
  const hasAppliedParamFilters = useRef(false);
  useEffect(() => {
    if (loadingStatus !== "loaded" || hasAppliedParamFilters.current) return;
    hasAppliedParamFilters.current = true;
    filters.forEach(({ key, options, onChange }) => {
      const selected = optionsFromParams(params.getAll(key), options);
      if (selected.length > 0) onChange(selected);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on load
  }, [loadingStatus]);

  // Sends a click for each option the user added or removed. Prefilling from
  // search params calls the setters directly, so it isn't sent.
  const handleFilterChange =
    (key: string, selected: Option[], onChange: (options: Option[]) => void) =>
    (next: Option[]) => {
      const isIn = (list: Option[], option: Option) =>
        list.some(({ value }) => value === option.value);
      next
        .filter((option) => !isIn(selected, option))
        .forEach((option) =>
          sendPremadeClick(getPremadeFilterClick(key, option, true))
        );
      selected
        .filter((option) => !isIn(next, option))
        .forEach((option) =>
          sendPremadeClick(getPremadeFilterClick(key, option, false))
        );
      onChange(next);
    };

  // The same filters render inline on the page and in the drawer, which always
  // has every filter. Each page elem drops out at its own breakpoint below
  // 1200px. Both copies share state, so each needs its
  // own id to keep DOM ids unique.
  const renderFilterDropdowns = (location: "page" | "drawer") =>
    filters.map(({ key, label, options, selected, onChange }) => {
      const dropdown = (
        <Dropdown
          key={key}
          id={`${key}-filter-${location}`}
          label={label}
          placeholder="All"
          multiple
          options={options}
          initialValues={selected}
          onChange={handleFilterChange(key, selected, onChange)}
          className={styles.dropdownElem}
        />
      );
      if (location === "drawer") return dropdown;
      // Show/hide lives on our own wrapper, since ttui's .dropdown styles load
      // later in the cascade and would override a class put on the Dropdown
      return (
        <div
          key={key}
          className={clsx([styles.filterSelect, styles[`filterSelect-${key}`]])}
        >
          {dropdown}
        </div>
      );
    });

  const handleRulesetChange = (value: string | number) => {
    const ruleset = value === ALL_RULESETS ? ALL_RULESETS : Number(value);
    if (ruleset !== selectedRuleset) {
      const label = filterOptions.rulesets.find(
        (option) => option.value === ruleset
      )?.label;
      sendPremadeClick(getPremadeRulesetClick(String(label ?? ruleset)));
    }
    setSelectedRuleset(ruleset);
    if (ruleset === ALL_RULESETS) return;

    // Drop selected collections from other rulesets, since they're no longer offered
    const rulesetCollectionValues = new Set(
      filterOptions.sources
        .filter((option) => option.rulesetId === ruleset)
        .map((option) => option.value)
    );
    setSelectedSources((selected) =>
      selected.filter((option) => rulesetCollectionValues.has(option.value))
    );
  };

  // Like the dropdowns, the rule set tabs render on the page and in the drawer.
  // They're the last filter to drop out of the page, below 480px.
  const renderRulesetFilter = (location: "page" | "drawer") => {
    const tabFilter = (
      <TabFilter
        className={styles.filterTab}
        label="Rule Set"
        options={filterOptions.rulesets}
        onChangeTab={handleRulesetChange}
        value={selectedRuleset}
      />
    );
    if (location === "drawer") return tabFilter;
    return (
      <div
        className={clsx([styles.filterSelect, styles["filterSelect-ruleset"]])}
      >
        {tabFilter}
      </div>
    );
  };

  const activeFilterCount =
    selectedClasses.length +
    selectedSpecies.length +
    selectedLevels.length +
    selectedSources.length +
    (selectedRuleset === ALL_RULESETS ? 0 : 1);

  const clearAllFilters = () => {
    setSelectedClasses([]);
    setSelectedSpecies([]);
    setSelectedLevels([]);
    setSelectedSources([]);
    setSelectedRuleset(ALL_RULESETS);
  };

  const closeFilterDrawer = useCallback(() => setShowFilterDrawer(false), []);

  // The filter button is hidden from 1200px up, where every filter fits on the
  // page, so don't leave the drawer open there. Keep in sync with styles.module.css.
  useEffect(() => {
    if (!showFilterDrawer) return;
    const desktopQuery = globalThis.matchMedia?.("(min-width: 1200px)");
    if (!desktopQuery) return;
    if (desktopQuery.matches) {
      closeFilterDrawer();
      return;
    }
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) closeFilterDrawer();
    };
    desktopQuery.addEventListener("change", handleChange);
    return () => desktopQuery.removeEventListener("change", handleChange);
  }, [showFilterDrawer, closeFilterDrawer]);

  const orderedPremades = useMemo(
    () => orderPremades(filteredCharacters),
    [filteredCharacters]
  );

  const orderedElevatedUserPremades = useMemo(
    () => orderPremades(elevatedUserCharacters, true),
    [elevatedUserCharacters]
  );

  const renderPremadeList = (premadeList: Array<PremadeCharacter>) => {
    return (
      <div className={styles.grid}>
        {premadeList.map(
          (
            {
              characterData: {
                name,
                characterLevel,
                speciesName,
                primaryClassName,
              },
              definition: { longDescription, imageUrl, imageAltText },
              characterId,
              publishStatus,
            },
            i
          ) => {
            const isPublished = publishStatus === PremadeInfoStatus.PUBLISHED;
            const isClaimInProgress = isPublished
              ? showProgressForIndex === i
              : false;
            const primarySource = getPrimarySource(premadeList[i]);
            const ariaLabel = `${speciesName} ${primaryClassName}`;
            const primaryButtonText = isPublished ? "Choose" : "Edit";
            const previewLink = `${ddbBaseUrl}/characters/${characterId}${
              campaignJoinCode ? `?campaignJoinCode=${campaignJoinCode}` : ""
            }`;
            const clickedPremade = { characterId, name };

            let primaryButtonProps: ButtonProps;
            if (isPublished) {
              primaryButtonProps = {
                onClick: (e) => {
                  e.stopPropagation();
                  sendPremadeClick(getChoosePremadeClick(clickedPremade));
                  onClaim(characterId, i);
                },
                disabled: !hasSlots || isClaimInProgress,
                "aria-label": isClaimInProgress
                  ? `Loading ${ariaLabel}`
                  : `Choose ${ariaLabel}`,
              };
            } else {
              primaryButtonProps = {
                href: `${ddbBaseUrl}/characters/${characterId}/builder`,
                "aria-label": `Edit ${ariaLabel}`,
              };
            }

            return (
              <Card
                key={i}
                data-testid="premade-character-card"
                characterName={name}
                characterLevel={characterLevel}
                characterId={characterId}
                publishStatus={publishStatus}
                className={clsx([
                  styles.premadeCard,
                  !hasSlots && styles.hideClaim,
                ])}
                speciesName={speciesName}
                primaryClassName={primaryClassName}
                rulesetLabel={primarySource.rulesetDisplayLabel}
                sourceName={primarySource.sourceName}
                longDescription={longDescription || ""}
                imageUrl={imageUrl || ""}
                imageAlt={imageAltText || `${speciesName} ${primaryClassName}`}
                primaryButtonText={primaryButtonText}
                primaryButtonProps={primaryButtonProps}
                isClaimInProgress={isClaimInProgress}
                previewLink={previewLink}
                // Admins' draft and archived premades aren't tracked
                onPreviewClick={
                  isPublished
                    ? () =>
                        sendPremadeClick(
                          getPreviewPremadeClick(
                            clickedPremade,
                            "button",
                            previewLink
                          )
                        )
                    : undefined
                }
                onClick={(e) => {
                  if (isClaimInProgress) return;
                  // The preview link and primary button handle their own clicks
                  if ((e.target as HTMLElement).closest("a, button")) return;
                  if (isPublished) {
                    sendPremadeClick(
                      getPreviewPremadeClick(
                        clickedPremade,
                        "card",
                        previewLink
                      )
                    );
                  }
                  // previewLink is a cross-origin URL (ddbBaseUrl), not an in-app route
                  window.location.assign(previewLink);
                }}
              />
            );
          }
        )}
      </div>
    );
  };

  return (
    <div className={styles.premadeListing} data-pagename="premade-listing">
      {(campaignJoinCode || campaignId) && (
        <Button
          className={styles.backButton}
          variant="leftArrow"
          color="secondary"
          href={backToCampaignUrl}
          onClick={() =>
            sendPremadeClick(
              getBackToCampaignClick(campaignJoinCode, backToCampaignUrl)
            )
          }
        >
          <ArrowLeft />
          Back to{campaignJoinCode ? " Join" : ""} Campaign
        </Button>
      )}
      <div className={styles.content}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <span className={styles.pageEyebrow}>Premade Characters</span>
            Pick a ready-to-play character.
          </h1>
          {!hasSlots && (
            <InlineAlert className={styles.inlineAlert} variant="warning">
              <p>
                Your party is full.{" "}
                <strong>
                  <a
                    className={styles.inlineAlertLink}
                    href={subscribeUrl}
                    onClick={() =>
                      sendPremadeClick(getPremadeSubscribeClick(subscribeUrl))
                    }
                  >
                    Subscribe
                  </a>
                </strong>{" "}
                to unlock unlimited character creation.
              </p>
            </InlineAlert>
          )}
        </div>
        <div className={styles.filters}>
          {renderRulesetFilter("page")}
          {renderFilterDropdowns("page")}
          <Search
            className={styles.filterSearch}
            value={query}
            onSearch={setQuery}
            placeholder="Name, Class, Species, etc..."
            label="Search"
            inputProps={LEXICON_INPUT_PROPS}
          />
          <div className={styles.filterButtonWrapper}>
            <Button
              color="secondary"
              size="small"
              className={styles.filterButton}
              onClick={() => setShowFilterDrawer(true)}
              aria-label={
                activeFilterCount > 0
                  ? `Filters (${activeFilterCount} active)`
                  : "Filters"
              }
              aria-haspopup="dialog"
              aria-expanded={showFilterDrawer}
            >
              <FilterIcon />
            </Button>
            {activeFilterCount > 0 && (
              <span className={styles.filterCount} aria-hidden="true">
                {activeFilterCount}
              </span>
            )}
          </div>
        </div>

        {loadingStatus === "loading" && (
          <div className={styles.grid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton className={styles.skeletonCard} key={i} />
            ))}
          </div>
        )}
        {/* Published Premades */}
        {loadingStatus === "loaded" &&
          (orderedPremades.length > 0 ? (
            renderPremadeList(orderedPremades)
          ) : (
            <NoResultsFound
              className={styles.noResults}
              message={`Empty tavern waits,\nNo champion claims this call,\nSeek another name.`}
            />
          ))}
        {/* Unpublished, admin only Premades */}
        {isElevatedUser && orderedElevatedUserPremades.length > 0 && (
          <div className={styles.elevatedUserContainer}>
            <Button
              className={styles.elevatedUserButton}
              color="secondary"
              onClick={() =>
                setShowElevatedUserCharacters(!showElevatedUserCharacters)
              }
            >
              {showElevatedUserCharacters ? "Hide" : "View"} Draft & Archived -
              ADMIN
            </Button>
            {showElevatedUserCharacters &&
              renderPremadeList(orderedElevatedUserPremades)}
          </div>
        )}
        <FilterDrawer
          open={showFilterDrawer}
          onClose={closeFilterDrawer}
          onReset={clearAllFilters}
          hasActiveFilters={activeFilterCount > 0}
          className={styles.mobileFilterMenu}
        >
          {renderRulesetFilter("drawer")}
          {renderFilterDropdowns("drawer")}
        </FilterDrawer>
        {/* We know at the top level if someone doesn't have enough slots and displays a message at the top of the page and have the create character buttons disabled. The dialog here provides extra context if a user tries to claim a premade without enough slots. */}
        {showDialog && (
          <MaxCharactersDialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            useMyCharactersLink
          />
        )}
        {showToast && (
          <Toast
            onClose={() => setShowToast(false)}
            className={styles.toast}
            variant="error"
            message="An error occurred while creating your copy of this character."
            description={
              campaignJoinCode ? "Try joining the campaign again." : undefined
            }
            action={
              campaignJoinCode ? (
                <Button
                  href={`${ddbBaseUrl}/campaigns/${campaignJoinCode}`}
                  className={styles.dialogLink}
                >
                  Campaign Page
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};
